import {ArchitectElement, ArchitectElControl, ElementControlType} from '../architect-element';
import {mutationState} from '@app/editor/state/mutation-store';
import {ModifyAttributes} from '@app/editor/mutations/attributes/modify-attributes';
import {executeScriptsInNode} from '@app/editor/utils/execute-scripts-in-node';
import {setSelectedContext} from '@app/editor/state/set-selected-context';
import {getNodeId} from '@app/editor/utils/get-node-id';
import {addIdToNode} from '@app/editor/utils/add-id-to-node';
import {editorState} from '@app/editor/state/editor-store';
import {inspectorState} from '@app/editor/state/inspector-store';

export class OmeletteEl extends ArchitectElement {
  name = 'Omelette Widget';
  contentCategories = ['flow'];
  allowedContent = [];
  category = 'addons';
  specificity = 10;
  canDrag = true;
  resizable = false;
  
  editActions = [
    {
      name: 'Refresh Preview',
      action: (node: HTMLElement) => {
        try {
          const doc = node.ownerDocument;
          const parent = node.parentNode;
          
          if (!parent) return;
          
          // Store all widget attributes
          const attrs: Record<string, string> = {
            'data-sdk': 'omelette',
            'data-variant': node.getAttribute('data-variant') || 'minimal',
            'data-waitlist': node.getAttribute('data-waitlist') || '',
            'data-title': node.getAttribute('data-title') || '',
            'data-subtitle': node.getAttribute('data-subtitle') || '',
            'data-submittext': node.getAttribute('data-submittext') || '',
            'data-hasnamefield': node.getAttribute('data-hasnamefield') || 'true',
            'data-hasphonefield': node.getAttribute('data-hasphonefield') || 'false',
            'data-imglocation': node.getAttribute('data-imglocation') || 'none',
            'data-imgsrc': node.getAttribute('data-imgsrc') || '',
            'data-subscribercounttext': node.getAttribute('data-subscribercounttext') || '',
            'data-metafields': node.getAttribute('data-metafields') || '',
          };
          
          // Store tracking info
          // const oldNodeId = getNodeId(node);
          const style = node.getAttribute('style');
          
          // Clear ALL references to the old node before replacing
          editorState().setSelectedContext(null);
          inspectorState().reset();
          
          // Create wrapper with script
          const wrapper = doc.createElement('section');
          wrapper.innerHTML = `<div></div><script src="https://omelette.net/omelette-sdk.umd.js"></script>`;
          
          const newWidget = wrapper.querySelector('div') as HTMLElement;
          
          // Apply all current attributes to new widget
          Object.entries(attrs).forEach(([key, value]) => {
            if (value) {
              newWidget.setAttribute(key, value);
            }
          });
          
          // Give it a new node ID
          addIdToNode(newWidget, true);
          
          // Store the new node ID for reselection after SDK processes
          const newNodeId = getNodeId(newWidget);
          
          // Preserve styling
          if (style) {
            newWidget.setAttribute('style', style);
          }
          
          // Replace the old node with new widget
          parent.replaceChild(newWidget, node);
          
          // Add the script to the parent
          const scriptElement = wrapper.querySelector('script') as HTMLScriptElement;
          if (scriptElement && !doc.querySelector('script[src*="omelette"]')) {
            parent.appendChild(scriptElement);
          }
          
          // Execute scripts in the new widget
          const scriptContainer = doc.createElement('section');
          scriptContainer.innerHTML = `<script src="https://omelette.net/omelette-sdk.umd.js"></script>`;
          const tempScript = scriptContainer.querySelector('script');
          if (tempScript) {
            newWidget.appendChild(tempScript);
            executeScriptsInNode(newWidget);
          }
          
          // Auto-reselect the new widget after SDK has finished processing
          setTimeout(() => {
            if (newNodeId) {
              // Pass node ID string so it queries DOM for fresh reference
              setSelectedContext(newNodeId, {force: true});
            }
          }, 900);
        } catch (error) {
          console.error('Error refreshing Omelette widget:', error);
        }
      },
    },
  ];
  
  controls = [
    new ArchitectElControl({
      label: 'Waitlist ID (Required)',
      type: ElementControlType.Input,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-waitlist') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-waitlist': value || '',
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Variant',
      type: ElementControlType.Select,
      options: [
        {key: 'Default', value: 'default'},
        {key: 'Dark', value: 'dark'},
        {key: 'Minimal', value: 'minimal'},
        {key: 'Compact', value: 'compact'},
        {key: 'Premium', value: 'premium'},
        {key: 'Multi-step', value: 'multi'},
      ],
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-variant') ?? 'minimal';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-variant': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Title',
      type: ElementControlType.Input,
      showAiTextButton: true,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-title') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-title': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Subtitle',
      type: ElementControlType.Input,
      showAiTextButton: true,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-subtitle') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-subtitle': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Submit Button Text',
      type: ElementControlType.Input,
      showAiTextButton: true,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-submittext') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-submittext': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Has Name Field',
      type: ElementControlType.Select,
      options: [
        {key: 'True', value: 'true'},
        {key: 'False', value: 'false'},
      ],
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-hasnamefield') ?? 'true';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-hasnamefield': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Has Phone Field',
      type: ElementControlType.Select,
      options: [
        {key: 'True', value: 'true'},
        {key: 'False', value: 'false'},
      ],
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-hasphonefield') ?? 'false';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-hasphonefield': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Image Location',
      type: ElementControlType.Select,
      options: [
        {key: 'None', value: 'none'},
        {key: 'Before', value: 'before'},
        {key: 'After', value: 'after'},
      ],
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-imglocation') ?? 'none';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-imglocation': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Image Source URL',
      type: ElementControlType.Input,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-imgsrc') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-imgsrc': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Subscriber Count Text',
      type: ElementControlType.Input,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-subscribercounttext') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-subscribercounttext': value,
          }),
          options,
        );
      },
    }),
    new ArchitectElControl({
      label: 'Meta Fields',
      type: ElementControlType.Input,
      defaultValue(node: HTMLElement) {
        return node.getAttribute('data-metafields') ?? '';
      },
      onChange(node: HTMLElement, value: string, options) {
        mutationState().executeMutation(
          new ModifyAttributes(node, {
            'data-metafields': value,
          }),
          options,
        );
      },
    }),
  ];
  
  matcher(node: HTMLElement) {
    return node.getAttribute('data-sdk') === 'omelette';
  }
}

